import { useQuery, useMutation, UseQueryOptions } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

// ========================================
// Types
// ========================================

export interface ExtractionTemplateVariable {
  name: string;
  description: string;
  type: 'string' | 'number' | 'boolean';
  default?: any;
  required: boolean;
  /**
   * Optional dot-notation path inside the template where this variable's value
   * is written by the server's applyVariablesToTemplate. Purely informational
   * for the UI — the backend handles the actual write.
   */
  path?: string;
}

export interface ExtractionTemplate {
  id: string;
  name: string;
  description: string;
  category: 'government' | 'ecommerce' | 'social' | 'custom';
  apiType: 'REST' | 'GRAPHQL';
  variables?: ExtractionTemplateVariable[];
  template?: {
    endpoint?: string;
    params?: Record<string, any>;
    transform?: string;
    headers?: Record<string, string>;
    pagination?: Record<string, any>;
  };
  example?: {
    input: any;
    output: any;
  };
}

export interface TemplateCategory {
  id: string;
  name: string;
  description: string;
}

export interface TemplatesResponse {
  templates: ExtractionTemplate[];
  total: number;
}

export interface CategoriesResponse {
  categories: TemplateCategory[];
}

export interface ApplyTemplateRequest {
  variables: Record<string, any>;
}

export interface ApplyTemplateResponse {
  template_id: string;
  template_name: string;
  query: any;
}

// ========================================
// API Hooks
// ========================================

/**
 * Hook: Get all extraction templates
 */
export function useExtractionTemplates(
  category?: string,
  options?: UseQueryOptions<TemplatesResponse>
) {
  return useQuery<TemplatesResponse>({
    queryKey: ['extraction', 'templates', category],
    queryFn: async () => {
      const url = category
        ? `extraction/templates?category=${category}`
        : 'extraction/templates';
      return await apiClient.get<TemplatesResponse>(url);
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    ...options,
  });
}

/**
 * Hook: Get specific template by ID
 */
export function useExtractionTemplate(
  templateId: string,
  options?: UseQueryOptions<ExtractionTemplate>
) {
  return useQuery<ExtractionTemplate>({
    queryKey: ['extraction', 'templates', templateId],
    queryFn: async () => {
      return await apiClient.get<ExtractionTemplate>(`extraction/templates/${templateId}`);
    },
    enabled: !!templateId,
    staleTime: 10 * 60 * 1000,
    ...options,
  });
}

/**
 * Hook: Get template categories
 */
export function useTemplateCategories(options?: UseQueryOptions<CategoriesResponse>) {
  return useQuery<CategoriesResponse>({
    queryKey: ['extraction', 'templates', 'categories'],
    queryFn: async () => {
      return await apiClient.get<CategoriesResponse>('extraction/templates/categories');
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    ...options,
  });
}

/**
 * Mutation: Apply template with variables
 */
export function useApplyTemplate(templateId: string) {
  return useMutation({
    mutationFn: async (params: ApplyTemplateRequest) => {
      return await apiClient.post<ApplyTemplateResponse>(
        `extraction/templates/${templateId}/apply`,
        params
      );
    },
  });
}
